import { Image } from '@components/products/products'
import { API_URL } from '@constants'
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/solid'
import Carousel from 'react-simply-carousel'
import axiosReadInstance from '../../request/axiosReadInstance'
import { useCallback, useEffect, useRef, useState } from 'react'
import { replaceImageExtension } from '@utils'
import { CarouselSlideTablist } from '@components/carousel/CarouselSlideTablist'
import { formatCarouselSlideAnnouncement } from '@components/carousel/carouselLiveRegion'
import {
    carouselContainerProps,
    carouselInnerProps,
    getCarouselShellClassName,
    getFullWidthSlideStyle,
    useCarouselMount,
} from '@components/carousel/useCarouselReady'

interface Feedback {
    name: string
    feedback: string
    job: string
    image: Image
    id: number
    company: number
}

interface ITestimonialDesire {
    title: string
    testimonialDesire: Array<Feedback>
}

function formatTestimonialDesireLive(item: Feedback) {
    const companyText =
        item.company !== undefined && item.company !== null
            ? String(item.company)
            : ''
    return [item.name, item.job, companyText, item.feedback]
        .filter((p) => p !== '')
        .join(', ')
}

export const TestimonialDesire = () => {
    const [testimonialDesire, setTestimonialDesire] =
        useState<ITestimonialDesire>()

    const [activeSlide, setActiveSlide] = useState(0)
    const [carouselLiveMessage, setCarouselLiveMessage] = useState('')
    const skipCarouselLiveRef = useRef(true)

    const fetchData = async () => {
        await axiosReadInstance
            .get(`${API_URL}/api/testimonial-desire?populate=deep`)
            .then((response) => {
                setTestimonialDesire(response.data.data.attributes)
            })
    }

    useEffect(() => {
        fetchData()
    }, [])

    useEffect(() => {
        const rows = testimonialDesire?.testimonialDesire
        if (!rows?.length) return
        const item = rows[activeSlide]
        if (!item) return
        if (skipCarouselLiveRef.current) {
            skipCarouselLiveRef.current = false
            return
        }
        setCarouselLiveMessage(
            formatCarouselSlideAnnouncement({
                slideIndex: activeSlide,
                totalSlides: rows.length,
                content: formatTestimonialDesireLive(item),
            })
        )
    }, [activeSlide, testimonialDesire?.testimonialDesire])

    const rows = testimonialDesire?.testimonialDesire
    const slideCount = rows?.length ?? 0
    const itemsToScroll = 1
    const {
        shellRef,
        containerWidth,
        canMount,
        isLayoutStable,
        markLayoutStable,
    } = useCarouselMount(slideCount > 0)
    const slideStyle =
        containerWidth > 0
            ? getFullWidthSlideStyle(containerWidth, 168)
            : undefined

    const handleSlideChange = useCallback(
        (slideIndex: number) => {
            setActiveSlide(slideIndex)
            markLayoutStable()
        },
        [markLayoutStable]
    )

    const carouselBtnClass =
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0a8335] focus-visible:outline-offset-2 focus-visible:rounded-full'

    return (
        <section
            className="pt-24 px-6 sm:px-4"
            aria-labelledby="testimonial-desire-heading"
            aria-roledescription="carousel"
        >
            <div className="max-w-screen-xl mx-auto">
                <h2 id="testimonial-desire-heading" className="h2 mb-10">
                    {testimonialDesire?.title}
                </h2>
                {testimonialDesire && slideCount > 0 && (
                    <>
                        <p
                            aria-live="polite"
                            aria-atomic="true"
                            className="sr-only"
                        >
                            {carouselLiveMessage}
                        </p>
                        <div
                            ref={shellRef}
                            className={getCarouselShellClassName(
                                isLayoutStable
                            )}
                        >
                            {canMount && slideStyle && (
                                <Carousel
                                    containerProps={{
                                        ...carouselContainerProps,
                                        style: {
                                            ...carouselContainerProps.style,
                                            justifyContent: 'space-between',
                                        },
                                    }}
                                    innerProps={carouselInnerProps}
                                    infinite={true}
                                    preventScrollOnSwipe
                                    swipeTreshold={60}
                                    activeSlideIndex={activeSlide}
                                    onRequestChange={handleSlideChange}
                                    activeSlideProps={{ style: slideStyle }}
                                    visibleSlideProps={{ style: slideStyle }}
                                    responsiveProps={[
                                        {
                                            itemsToShow: 1,
                                            itemsToScroll: 1,
                                            minWidth: 768,
                                        },
                                    ]}
                                    backwardBtnProps={{
                                        'aria-label': 'Témoignages précédents',
                                        children: (
                                            <ArrowLeftIcon
                                                aria-hidden={true}
                                                className="border border-neutral-400 rounded-full p-2 w-12 h-12 md:w-16 md:h-16"
                                            />
                                        ),
                                        style: {
                                            width: 60,
                                            height: 60,
                                            minWidth: 60,
                                            alignSelf: 'center',
                                            marginRight: 24,
                                        },
                                        className: carouselBtnClass,
                                    }}
                                    forwardBtnProps={{
                                        'aria-label': 'Témoignages suivants',
                                        children: (
                                            <ArrowRightIcon
                                                aria-hidden={true}
                                                className="border border-neutral-400 rounded-full p-2 w-12 h-12 md:w-16 md:h-16"
                                            />
                                        ),
                                        style: {
                                            width: 60,
                                            height: 60,
                                            minWidth: 60,
                                            alignSelf: 'center',
                                            marginLeft: 24,
                                        },
                                        className: carouselBtnClass,
                                    }}
                                    dotsNav={{ show: false }}
                                    itemsToShow={1}
                                    speed={400}
                                >
                                    {testimonialDesire.testimonialDesire.map(
                                        (item, index) => {
                                            return (
                                                <div
                                                    key={item.id ?? index}
                                                    id={`testimonial-desire-slide-${index}`}
                                                    aria-roledescription="slide"
                                                    aria-label={`Témoignage ${
                                                        index + 1
                                                    } sur ${slideCount}`}
                                                    className="grid grid-cols-1 lg:grid-cols-2 box-border"
                                                    style={slideStyle}
                                                >
                                                    <img
                                                        className="w-full h-auto object-cover"
                                                        src={replaceImageExtension(
                                                            item.image.data
                                                                .attributes.url
                                                        )}
                                                        alt={
                                                            item.image.data
                                                                .attributes
                                                                .alternativeText
                                                        }
                                                    />
                                                    <div className="self-center flex flex-col gap-4">
                                                        {item.job && (
                                                            <p className="paragraph text-neutral-600">
                                                                {item.job}
                                                            </p>
                                                        )}
                                                        <p className="paragraph text-neutral-600 min-h-[65px]">
                                                            {item.feedback}
                                                        </p>
                                                        <p className="h3 mt-4">
                                                            {item.name}
                                                            {item.company !=
                                                            null
                                                                ? `, ${item.company}`
                                                                : ''}
                                                        </p>
                                                    </div>
                                                </div>
                                            )
                                        }
                                    )}
                                </Carousel>
                            )}
                        </div>
                        <CarouselSlideTablist
                            tabCount={slideCount}
                            activeSlideIndex={activeSlide}
                            itemsToScroll={itemsToScroll}
                            totalSlides={slideCount}
                            onRequestChange={setActiveSlide}
                            tablistLabel="Témoignages"
                            getPanelIdForSlide={(i) =>
                                `testimonial-desire-slide-${i}`
                            }
                        />
                    </>
                )}
            </div>
        </section>
    )
}
